// src/pages/client/billing-invoices/BillingInvoiceList.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router';
import MainCard from 'components/MainCard';
import StatisticsCards from 'components/StatisticsCards';
import { DataTable, TableFilters } from 'components/DataTable';
import { clientPortalInvoiceService } from 'services/api';
import projectService from 'services/projectService';
import { useAsync } from 'hooks/useAsync';

const BillingInvoiceList = () => {
  const navigate = useNavigate();
  const [allInvoices, setAllInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, invoice: null });

  const { loading, execute: fetchInvoices } = useAsync(async () => {
    const response = await clientPortalInvoiceService.getAll({
      per_page: 'all',
      sort_by: 'invoice_date',
      sort_order: 'desc'
    });
    setAllInvoices(response.data || []);
  });

  const { execute: fetchProjects } = useAsync(async () => {
    const response = await projectService.getProjects({ per_page: 'all' });
    setProjects(response.data);
  });

  useEffect(() => {
    fetchInvoices();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, projectFilter]);

  // Apply client-side filtering
  const filteredInvoices = allInvoices.filter((invoice) => {
    // Status filter
    if (statusFilter !== 'all' && invoice.status !== statusFilter) {
      return false;
    }

    // Project filter
    if (projectFilter !== 'all' && invoice.project_id !== parseInt(projectFilter)) {
      return false;
    }

    // Search filter (invoice number or custom invoice number)
    if (search) {
      const searchLower = search.toLowerCase();
      const invoiceNumber = (invoice.invoice_number || '').toLowerCase();
      const customInvoiceNumber = (invoice.custom_invoice_number || '').toLowerCase();

      if (!invoiceNumber.includes(searchLower) && !customInvoiceNumber.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });

  // Apply pagination
  const paginatedInvoices = filteredInvoices.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Calculate statistics from filtered data
  const statistics = filteredInvoices.reduce(
    (acc, invoice) => {
      if (invoice.status === 'sent') {
        acc.pending += 1;
        acc.pendingAmount += parseFloat(invoice.amount || 0);
      } else if (invoice.status === 'paid') {
        acc.paid += 1;
        acc.paidAmount += parseFloat(invoice.amount || 0);
      }
      return acc;
    },
    { pending: 0, pendingAmount: 0, paid: 0, paidAmount: 0 }
  );

  const statisticsData = [
    {
      label: 'Pending Payment',
      value: statistics.pending,
      color: 'warning.main',
      subtitle: `Total: $${statistics.pendingAmount.toFixed(2)}`
    },
    {
      label: 'Remittance Advice',
      value: statistics.paid,
      color: 'success.main',
      subtitle: `Total: $${statistics.paidAmount.toFixed(2)}`
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'warning';
      case 'paid':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'sent':
        return 'Pending Payment';
      case 'paid':
        return 'Remittance Advice';
      default:
        return status;
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount || 0);
  };

  const handleMarkAsPaidClick = (invoice) => {
    setConfirmDialog({ open: true, invoice });
  };

  const handleMarkAsPaidConfirm = async () => {
    const invoice = confirmDialog.invoice;
    if (!invoice) return;

    try {
      await clientPortalInvoiceService.markAsPaid(invoice.id);
      setSnackbar({
        open: true,
        message: 'Invoice marked as paid successfully',
        severity: 'success'
      });
      setConfirmDialog({ open: false, invoice: null });
      fetchInvoices(); // Refresh the invoice list
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to mark invoice as paid',
        severity: 'error'
      });
      setConfirmDialog({ open: false, invoice: null });
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, invoice: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columns = [
    {
      id: 'invoice_number',
      label: 'Invoice #',
      render: (_value, row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500
          }}
        >
          {row.custom_invoice_number || row.invoice_number}
        </Typography>
      ),
      primary: true
    },
    {
      id: 'project',
      label: 'Project',
      accessor: 'project.name'
    },
    {
      id: 'invoice_date',
      label: 'Invoice Date',
      accessor: 'invoice_date',
      type: 'date'
    },
    {
      id: 'due_date',
      label: 'Due Date',
      accessor: 'due_date',
      type: 'date'
    },
    {
      id: 'amount',
      label: 'Amount',
      render: (_value, row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500
          }}
        >
          {formatCurrency(row.amount, row.currency)}
        </Typography>
      )
    },
    {
      id: 'status',
      label: 'Status',
      render: (_value, row) => <Chip label={getStatusLabel(row.status)} color={getStatusColor(row.status)} size="small" />
    }
  ];

  return (
    <Box>
      {/* Statistics Cards */}
      <StatisticsCards statistics={statisticsData} columns={2} />

      <MainCard title="My Invoices">
        <TableFilters
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by invoice number..."
          filters={[
            {
              label: 'Status',
              value: statusFilter,
              onChange: setStatusFilter,
              options: [
                { value: 'all', label: 'All Status' },
                { value: 'sent', label: 'Pending Payment' },
                { value: 'paid', label: 'Remittance Advice' }
              ]
            },
            {
              label: 'Project',
              value: projectFilter,
              onChange: setProjectFilter,
              options: [
                { value: 'all', label: 'All Projects' },
                ...projects.map((project) => ({
                  value: project.id,
                  label: project.name
                }))
              ]
            }
          ]}
        />

        <DataTable
          columns={columns}
          data={paginatedInvoices}
          loading={loading}
          emptyMessage="No invoices found"
          pagination={{
            enabled: true,
            serverSide: false,
            page: page,
            rowsPerPage: rowsPerPage,
            total: filteredInvoices.length,
            onPageChange: (_e, newPage) => setPage(newPage),
            onRowsPerPageChange: (e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            },
            rowsPerPageOptions: [5, 10, 25, 50]
          }}
          actions={{
            onView: (row) => navigate(`/client/billing-invoices/${row.id}`),
            onPay: {
              condition: (row) => row.status === 'sent',
              handler: (row) => handleMarkAsPaidClick(row)
            }
          }}
        />
      </MainCard>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark invoice{' '}
            <strong>{confirmDialog.invoice?.custom_invoice_number || confirmDialog.invoice?.invoice_number}</strong> as paid?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button onClick={handleMarkAsPaidConfirm} variant="contained" color="success">
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillingInvoiceList;
